import React, { useState, ChangeEvent, useEffect } from "react";
import SuccessfulCreation from "@pages/Developers/Projects/success-modal";
import {
  getSinglePropertyTransaction,
  sendAmenitiesChecklist,
} from "@services/krent.service";
import "./amenities.css";
interface AmenityCategory {
  title: string;
  offers: string[];
}

interface AmenityItem {
  id: number;
  category: string;
  units: number;
  checked: boolean;
  comment: string;
  name:
    | string
    | {
        name: string | AmenityItem["name"];
        unit: number;
        checked: boolean;
        comments: string;
      };
}

interface Props {
  transactionId: string;
  amenities: AmenityCategory[];
  setOpenValidateAmenities: (open: boolean) => void;
  closeModal?: any;
  setTransaction?: any;
}
function getAmenityName(name: any): string {
  if (typeof name === "string") return name;
  if (name && typeof name === "object") return getAmenityName(name.name);
  return "";
}

const AmenitiesChecklist: React.FC<Props> = ({
  transactionId,
  amenities,
  setOpenValidateAmenities,
  closeModal,
  setTransaction,
}) => {
  const [successModal, setSuccessModal] = useState(false);
  const createInitialAmenities = (): AmenityItem[] => {
    let id = 1;
    return amenities?.flatMap((category) =>
      category.offers.map((offer) => ({
        id: id++,
        name: {
          name: offer,
          unit: 0,
          checked: false,
          comments: "",
        },
        category: category.title,
        units: 0,
        checked: false,
        comment: "",
      })),
    );
  };

  const [amenityItems, setAmenityItems] = useState<AmenityItem[]>(
    createInitialAmenities(),
  );
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (successModal) {
      timer = setTimeout(() => {
        setSuccessModal(false);
      }, 20000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [successModal]);

  const handleUnitsChange = (id: number, increment: number): void => {
    setAmenityItems((items) =>
      items.map((item) =>
        item.id === id && item.checked
          ? { ...item, units: Math.max(0, item.units + increment) }
          : item,
      ),
    );
  };

  const handleCommentChange = (id: number, comment: string): void => {
    setAmenityItems((items) =>
      items.map((item) =>
        item.id === id && item.checked ? { ...item, comment } : item,
      ),
    );
  };

  const handleCheckChange = (id: number): void => {
    setAmenityItems((items) =>
      items.map((item) =>
        item.id === id
          ? item.checked
            ? { ...item, checked: false, units: 0, comment: "" }
            : { ...item, checked: true }
          : item,
      ),
    );
  };

  const hasAnySelection = amenityItems.some((item) => item.checked);

  const handleSubmit = async () => {
    if (!hasAnySelection) {
      setError("You can't submit an empty checklist.");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      const payload = amenityItems
        .filter((item) => item.checked)
        .map((item) => ({
          name:
            typeof item.name === "string"
              ? item.name.toLowerCase()
              : typeof item.name.name === "string"
                ? item.name.name.toLowerCase()
                : "",
          category: item.category,
          number: item.units,
          comments: item.comment,
        }));

      await sendAmenitiesChecklist(transactionId, payload);
      setOpenValidateAmenities(false);
      setSuccessModal(true);

      const abortController = new AbortController();
      await new Promise((resolve) => setTimeout(resolve, 1000));

      const response = await getSinglePropertyTransaction(
        transactionId,
        abortController.signal,
      );

      if (response as any) {
        setTransaction((response as any)?.data);
      }
    } catch (err: any) {
      setError(err.message || "Failed to submit checklist. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredAmenities = amenityItems?.filter((item) => {
    const amenityName =
      typeof item.name === "string"
        ? item.name
        : typeof item.name.name === "string"
          ? item.name.name
          : "";

    const category = item?.category || "";

    return (
      amenityName.toLowerCase().includes(searchTerm?.toLowerCase() || "") ||
      category.toLowerCase().includes(searchTerm?.toLowerCase() || "")
    );
  });

  return (
    <div className="container mt-4">
      <div className="card mx-auto" style={{ maxWidth: "1000px" }}>
        <div className="card-body">
          <div className="mb-4 position-relative">
            <div
              className="position-absolute"
              style={{
                left: "15px",
                top: "50%",
                transform: "translateY(-50%)",
              }}
            >
              <svg
                className="text-secondary"
                style={{ width: "16px", height: "16px" }}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search amenities or categories"
              className="form-control ps-5"
              value={searchTerm}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setSearchTerm(e.target.value)
              }
            />
          </div>

          {error && <div className="alert alert-danger">{error}</div>}
          <p className="text-muted small mb-3">
            You must tick at least one checkbox to submit the amenities
            checklist.
          </p>
          <div className="table-responsive">
            <table className="table table-borderless amenities-table">
              <thead>
                <tr>
                  <th>S/N</th>
                  <th>Check</th>
                  <th>Amenity</th>
                  <th>Units</th>
                  <th>Leave a comment</th>
                </tr>
              </thead>
              <tbody>
                {filteredAmenities.map((item) => (
                  <tr key={item.id}>
                    <td data-label="S/N">{item.id}</td>

                    <td data-label="Check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={item.checked}
                        onChange={() => handleCheckChange(item.id)}
                      />
                    </td>

                    <td data-label="Amenity">{getAmenityName(item.name)}</td>

                    <td data-label="Units">
                      <div className="input-group units-group">
                        <button
                          disabled={!item.checked}
                          className="btn btn-outline-secondary"
                          onClick={() => handleUnitsChange(item.id, -1)}
                        >
                          -
                        </button>
                        <span className="input-group-text units-value">
                          {item.units}
                        </span>
                        <button
                          disabled={!item.checked}
                          className="btn btn-outline-secondary"
                          onClick={() => handleUnitsChange(item.id, 1)}
                        >
                          +
                        </button>
                      </div>
                    </td>

                    <td data-label="Comment">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Type message here..."
                        value={item.comment}
                        disabled={!item.checked}
                        onChange={(e) =>
                          handleCommentChange(item.id, e.target.value)
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="d-flex gap-2 mt-3">
            <button
              className="btn btn-primary w-100 fw-bold"
              onClick={handleSubmit}
              disabled={isSubmitting || !hasAnySelection}
            >
              {isSubmitting ? "SUBMITTING..." : "SUBMIT CHECKLIST"}
            </button>
            <button
              className="btn border-green text-primary w-100 fw-bold rounded-10"
              onClick={closeModal}
              disabled={isSubmitting}
            >
              DO THIS LATER
            </button>
          </div>
        </div>
      </div>
      <SuccessfulCreation
        setSuccessModal={setSuccessModal}
        successModal={successModal}
        title={"Amenities checklist submitted!"}
        content="You have successfully submitted the amenities checklist for this property"
        buttonText="Go back to messaging"
        buttonLink={`/messaging/${transactionId}`}
      />
    </div>
  );
};

export default AmenitiesChecklist;
